import React, { useState, useEffect } from 'react';
import { useOrder } from '../../context/OrderContext';
import { RALModal } from '../../modals';

// Helper to check if finish requires RAL code
const isSpecialFinish = (code) => code === 'V1' || code === 'V2';

// Spacer configuration rules for each profile type
const SPACER_CONFIGS = {
    '6': {
        codes: ['N6-SC300-*', 'N6-SC100-*'],
        description: 'Valido solo per NINFA 6',
        spacerTypes: [
            { code: 'N6-SC300-*', name: 'Spessore 300', thickness: '300' },
            { code: 'N6-SC100-*', name: 'Spessore 100', thickness: '100', image: '/images/accessories/SPESSORI/SEL-N6-SC100.png' }
        ]
    },
    '106': {
        codes: ['N106-SC300-*', 'N106-SC100-*'],
        description: 'Valido solo per NINFA 106',
        spacerTypes: [
            { code: 'N106-SC300-*', name: 'Spessore 300', thickness: '300', image: '/images/accessories/SPESSORI/SEL-N106-SC300.png' },
            { code: 'N106-SC100-*', name: 'Spessore 100', thickness: '100' }
        ]
    },
    '116': {
        codes: ['N116-SC400-*', 'N116-SC100-*'],
        description: 'Valido solo per NINFA 116',
        spacerTypes: [
            { code: 'N116-SC400-*', name: 'Spessore 400', thickness: '400', image: '/images/accessories/SPESSORI/SEL-N116-SC400.png' },
            { code: 'N116-SC100-*', name: 'Spessore 100', thickness: '100', image: '/images/accessories/SPESSORI/SEL-N116-SC100.png' }
        ]
    },
    '176': {
        codes: ['N186-SC-A'],
        description: 'Valido solo per NINFA 176',
        spacerTypes: [
            { code: 'N186-SC-A', name: 'Spessore Standard', thickness: 'standard', image: '/images/accessories/SPESSORI/SEL-N186-SC-A.png' }
        ],
        fixedFinish: 'A' // Only Argento finish
    },
    '186': {
        codes: ['N186-SC-A'],
        description: 'Valido solo per NINFA 186',
        spacerTypes: [
            { code: 'N186-SC-A', name: 'Spessore Standard', thickness: 'standard', image: '/images/accessories/SPESSORI/SEL-N186-SC-A.png' }
        ],
        fixedFinish: 'A' // Only Argento finish
    }
};

// Available finishes (same as caps)
const FINISHES = [
    { code: 'A', name: 'Argento', color: '#C0C0C0' },
    { code: 'B', name: 'RAL9010 Opaco', color: '#FFFFFF' },
    { code: 'N', name: 'RAL9005 Opaco', color: '#000000' },
    { code: 'G', name: 'Grezzi', color: '#E0E0E0' },
    { code: 'V1', name: 'Verniciato Speciale 1', color: '#D3D3D3' },
    { code: 'V2', name: 'Verniciato Speciale 2', color: '#B8B8B8' }
];

export const SpacerSelector = ({ onSelect, initialValues = {}, isEditMode = false, profileType: propProfileType }) => {
    const { state } = useOrder();
    // Use profileType from props (passed from navigation) or fall back to order state
    const profileType = propProfileType || state?.orderDetails?.profileType || state?.orderDetails?.ninfaNumber;

    // Normalize profile type to extract number only (e.g., "N6" -> "6", "N106" -> "106")
    // Also handle 6S (6 STADIO) -> 6
    const rawNormalized = profileType ? profileType.toString().replace(/^N/, '') : null;
    const normalizedProfileType = rawNormalized === '6S' ? '6' : rawNormalized;

    // State management
    const [step, setStep] = useState(1);
    const [selectedSpacers, setSelectedSpacers] = useState(initialValues?.selectedSpacers || []);
    const [finish, setFinish] = useState(initialValues?.finish || '');
    const [ralCode, setRalCode] = useState(initialValues?.ralCode || null);
    const [selectedProfileForAccessory, setSelectedProfileForAccessory] = useState(normalizedProfileType || '');

    // RAL Modal state
    const [showRALModal, setShowRALModal] = useState(false);
    const [pendingFinishCode, setPendingFinishCode] = useState(null);

    // Get available spacers - filtered by profile or all if accessory-only flow
    const getAvailableSpacers = () => {
        const allSpacers = [];

        if (normalizedProfileType && SPACER_CONFIGS[normalizedProfileType]) {
            // If we have a specific profile, only show spacers for that profile
            const config = SPACER_CONFIGS[normalizedProfileType];
            config.spacerTypes.forEach(spacerType => {
                allSpacers.push({
                    ...spacerType,
                    profile: normalizedProfileType,
                    description: config.description,
                    fixedFinish: config.fixedFinish
                });
            });
        } else {
            // If no profile selected (standalone accessories order), show all spacers
            Object.entries(SPACER_CONFIGS).forEach(([profile, config]) => {
                config.spacerTypes.forEach(spacerType => {
                    allSpacers.push({
                        ...spacerType,
                        profile,
                        description: config.description,
                        fixedFinish: config.fixedFinish
                    });
                });
            });
        }
        return allSpacers;
    };

    // Get profile configuration
    const profileConfig = SPACER_CONFIGS[normalizedProfileType || selectedProfileForAccessory];
    const isAccessoryOnlyFlow = !normalizedProfileType; // True when ordering accessories without selecting profile first

    // Reset when profile changes
    useEffect(() => {
        if (profileType && profileType !== selectedProfileForAccessory) {
            setSelectedProfileForAccessory(profileType);
            setStep(1);
            setSelectedSpacers([]);
            setFinish('');
        }
    }, [profileType, selectedProfileForAccessory]);

    const handleSpacerSelection = (spacerData) => {
        // spacerData can be either spacerType object (profile-specific flow) or object (accessory-only flow)
        const spacerCode = spacerData.code;
        const spacerProfile = spacerData.profile || (profileType || selectedProfileForAccessory);

        setSelectedSpacers(prev => {
            // Use both code and profile to identify spacers (important for 176/186 which share the same code)
            const existing = prev.find(spacer => spacer.code === spacerCode && spacer.profile === spacerProfile);
            if (existing) {
                return prev.filter(spacer => !(spacer.code === spacerCode && spacer.profile === spacerProfile));
            } else {
                return [...prev, {
                    code: spacerCode,
                    name: spacerData.name,
                    thickness: spacerData.thickness,
                    quantity: 1,
                    profile: spacerProfile,
                    fixedFinish: spacerData.fixedFinish
                }];
            }
        });
    };

    const updateSpacerQuantity = (spacerCode, spacerProfile, quantity) => {
        setSelectedSpacers(prev =>
            prev.map(spacer =>
                (spacer.code === spacerCode && spacer.profile === spacerProfile)
                    ? { ...spacer, quantity: quantity === '' ? '' : Math.max(1, quantity) }
                    : spacer
            )
        );
    };

    const handleFinishSelect = (finishCode) => {
        if (isSpecialFinish(finishCode)) {
            setPendingFinishCode(finishCode);
            setShowRALModal(true);
        } else {
            setFinish(finishCode);
            setRalCode(null);
            setStep(3);
        }
    };

    // Handle RAL confirmation
    const handleRALConfirm = (code) => {
        setShowRALModal(false);
        setFinish(pendingFinishCode);
        setRalCode(code);
        setPendingFinishCode(null);
        setStep(3);
    };

    // Handle RAL modal close
    const handleRALClose = () => {
        setShowRALModal(false);
        setPendingFinishCode(null);
    };

    const generateProductCodes = () => {
        return selectedSpacers.map(spacer => {
            // Handle fixed finish (176/186) vs variable finish
            const finalFinish = spacer.fixedFinish || finish;
            const baseCode = spacer.fixedFinish ? spacer.code : spacer.code.replace('*', finalFinish);
            const profileInfo = isAccessoryOnlyFlow ? ` (NINFA ${spacer.profile})` : '';
            const ralInfo = ralCode ? ` - ${ralCode}` : '';

            return {
                code: baseCode,
                quantity: spacer.quantity,
                profile: spacer.profile,
                ralCode: ralCode || null,
                description: `${spacer.name} - ${FINISHES.find(f => f.code === finalFinish)?.name || finalFinish}${ralInfo}${profileInfo}`
            };
        });
    };

    const handleSubmit = () => {
        const productCodes = generateProductCodes();
        const spacerConfig = {
            selectedSpacers,
            finish,
            ralCode: ralCode || null,
            productCodes,
            profileType: profileType || selectedProfileForAccessory
        };

        onSelect(spacerConfig);
    };

    // Get available spacers based on context
    const availableSpacers = getAvailableSpacers();

    // Check if any selected spacer has fixed finish (N186-SC-A)
    const hasFixedFinishSpacers = selectedSpacers.some(spacer => spacer.fixedFinish);
    const needsFinishSelection = selectedSpacers.length > 0 && !hasFixedFinishSpacers;

    // Render appropriate step
    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">
                            {isAccessoryOnlyFlow ? 'Seleziona gli Spessori' : `Seleziona gli Spessori per NINFA ${normalizedProfileType || selectedProfileForAccessory}`}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {isAccessoryOnlyFlow ? 'Scegli gli spessori per qualsiasi profilo NINFA' :
                                (profileConfig?.description || '')}
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                            {availableSpacers.map((spacerData) => {
                                const isSelected = selectedSpacers.some(spacer => spacer.code === spacerData.code && spacer.profile === spacerData.profile);

                                return (
                                    <button
                                        key={`${spacerData.profile || profileType}-${spacerData.code}`}
                                        onClick={() => handleSpacerSelection(spacerData)}
                                        className={`p-4 border-2 rounded-xl transition-all ${isSelected ? 'bg-red-100 border-red-500' : 'border-gray-200 bg-white hover:border-red-500 hover:shadow-lg'}`}
                                    >
                                        <div className="flex flex-col items-center">
                                            {spacerData.image ? (
                                                <div className="w-full h-20 mb-2 flex items-center justify-center">
                                                    <img
                                                        src={spacerData.image}
                                                        alt={spacerData.name}
                                                        className="h-16 object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextElementSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="w-16 h-16 hidden items-center justify-center bg-red-50 rounded-full">
                                                        <span className="text-xl font-bold">{spacerData.thickness}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="w-16 h-16 flex items-center justify-center mb-2 bg-red-50 rounded-full">
                                                    <span className="text-xl font-bold">{spacerData.thickness}</span>
                                                </div>
                                            )}
                                            <span className="font-medium text-gray-700">{spacerData.name}</span>
                                            <span className="text-sm text-gray-500">{spacerData.code.replace('*', 'X')}</span>
                                            {isAccessoryOnlyFlow && (
                                                <span className="text-xs text-red-600 mt-1">NINFA {spacerData.profile}</span>
                                            )}
                                            {spacerData.fixedFinish && (
                                                <span className="text-xs text-green-600 mt-1">Solo Argento</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6">
                            <button
                                onClick={() => {
                                    if (selectedSpacers.length > 0) {
                                        if (needsFinishSelection) {
                                            setStep(2);
                                        } else {
                                            setStep(3); // Skip finish selection for fixed finish spacers
                                        }
                                    }
                                }}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                disabled={selectedSpacers.length === 0}
                            >
                                Continua ({selectedSpacers.length} selezionati)
                            </button>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Seleziona la Finitura</h2>
                        <p className="text-gray-600 mb-6">Scegli la finitura per gli spessori selezionati</p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                            {FINISHES.map((finishOption) => (
                                <button
                                    key={finishOption.code}
                                    onClick={() => handleFinishSelect(finishOption.code)}
                                    className={`p-4 border rounded-lg ${finish === finishOption.code ? 'bg-red-100 border-red-500' : 'border-gray-300'}`}
                                >
                                    <div className="flex flex-col items-center">
                                        <div
                                            className="w-16 h-16 rounded-full mb-2 border border-gray-300"
                                            style={{ backgroundColor: finishOption.color }}
                                        ></div>
                                        <span className="font-medium">{finishOption.name}</span>
                                        <span className="text-sm text-gray-500">({finishOption.code})</span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(1)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            {finish && (
                                <button
                                    onClick={() => setStep(3)}
                                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                >
                                    Continua
                                </button>
                            )}
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Configura le Quantità</h2>

                        <div className="max-w-2xl mx-auto space-y-4 mb-6">
                            {selectedSpacers.map((spacer) => (
                                <div key={`${spacer.profile}-${spacer.code}`} className="p-4 border rounded-lg border-gray-200">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="text-left">
                                            <span className="font-medium">
                                                {spacer.name}
                                                {isAccessoryOnlyFlow && spacer.profile && (
                                                    <span className="text-red-600 ml-2">(NINFA {spacer.profile})</span>
                                                )}
                                            </span>
                                            <p className="text-sm text-gray-500">
                                                Codice: {spacer.fixedFinish ? spacer.code : spacer.code.replace('*', finish)}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <label className="text-sm font-medium">Quantità:</label>
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                min="1"
                                                value={spacer.quantity ?? ''}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    updateSpacerQuantity(spacer.code, spacer.profile, raw === '' ? '' : Math.max(1, parseInt(raw) || 1));
                                                }}
                                                className="w-24 p-3 text-base border rounded-md text-center"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg mb-6">
                            <h3 className="font-medium mb-2">Riepilogo Prodotti</h3>
                            {generateProductCodes().map((product, index) => (
                                <div key={index} className="text-sm">
                                    <span className="font-medium">{product.code}</span> -
                                    Qty: {product.quantity} - {product.description}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(needsFinishSelection ? 2 : 1)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={selectedSpacers.some(s => !s.quantity || s.quantity === '' || parseInt(s.quantity) < 1)}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Aggiungi al Carrello
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // If no profile is selected or profile not supported (and not in accessory-only flow)
    if (!isAccessoryOnlyFlow && (!profileType || !profileConfig)) {
        return (
            <div className="text-center p-8">
                <h2 className="text-xl font-bold mb-4">Seleziona gli Spessori</h2>
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    <p>Prima seleziona un profilo NINFA per vedere gli spessori disponibili.</p>
                    {profileType && !profileConfig && (
                        <p className="mt-2">Il profilo {profileType} non supporta spessori accessori.</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {renderStep()}
            <RALModal
                isOpen={showRALModal}
                onClose={handleRALClose}
                onConfirm={handleRALConfirm}
                finishType={pendingFinishCode}
            />
        </div>
    );
};

export default SpacerSelector;
